import { useEffect, useState } from "react";
import { adminApi, getApiErrorMessage } from "@/lib/api";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { ConciergeBell, Send, FileDown, Upload, Trash2 } from "lucide-react";

const STATUS_TONE = { OVERDUE: "bg-red-100 text-red-700", DUE: "bg-amber-100 text-amber-800", SENT: "bg-emerald-50 text-emerald-700" };
const STATEMENT_FOR = { MF_SOA: "MF_SOA", MF_UNIT_STATEMENT: "MF_SOA", AIF_NAV_STATEMENT: "AIF_SOA", AIF_ANNUAL_REPORT: "AIF_SOA", AIF_DRAWDOWN_NOTICE: "AIF_DRAWDOWN_NOTICE", PMS_QUARTERLY_REPORT: "PMS_QUARTERLY_REPORT", PMS_ANNUAL_STATEMENT: "PMS_QUARTERLY_REPORT" };

function downloadBlob(data, name) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(data);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}

function CalendarTab() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const load = () => adminApi.get("/admin/servicing/calendar").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const dispatch = async (o) => {
    try {
      const { data } = await adminApi.post("/admin/servicing/dispatch", { user_id: o.user_id, doc_type: o.doc_type, period: o.period });
      const d = data?.delivery || {};
      if (d.sent) toast.success(`E-mailed to ${o.email}`);
      else if (d.reason === "graph_mail_not_configured") toast.warning("Microsoft Graph mail is not configured — the dispatch was logged, but nothing was sent.");
      else toast.error(`Could not send: ${d.message || "unknown error"}`);
      load();
    } catch (err) { toast.error(getApiErrorMessage(err)); }
  };
  const preview = async (o) => {
    const kind = STATEMENT_FOR[o.doc_type];
    if (!kind) return toast.info("No auto-generated statement for this item — attach via Document Vault");
    try {
      const ref = o.doc_type === "AIF_DRAWDOWN_NOTICE" ? o.period : (o.doc_type.startsWith("MF") ? o.period.replace("TXN-", "").slice(0, 7) : o.period);
      const resp = await adminApi.get(`/admin/servicing/statements/${o.user_id}/${kind}?ref=${encodeURIComponent(ref)}`, { responseType: "blob" });
      downloadBlob(resp.data, `${o.doc_type}_${o.client_code}.pdf`);
    } catch (err) { toast.error(getApiErrorMessage(err)); }
  };

  if (!data) return <div className="py-10 text-center text-sm text-[var(--ink-muted)]">Computing statutory obligations...</div>;
  const rows = data.obligations.filter((o) => filter === "ALL" || o.status === filter);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        {[["OVERDUE", data.summary.overdue, "svc-overdue-count"], ["DUE", data.summary.due, "svc-due-count"], ["SENT", data.summary.sent, "svc-sent-count"], ["ALL", data.obligations.length, "svc-all-count"]].map(([s, n, tid]) => (
          <button key={s} className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${filter === s ? "bg-[var(--brand)] text-white" : "bg-[var(--surface-2)] text-[var(--ink-muted)]"}`} onClick={() => setFilter(s)} data-testid={tid}>
            {s} · {n}
          </button>
        ))}
      </div>
      <Card className="border-[var(--glass-border)]"><CardContent className="overflow-x-auto p-0">
        <table className="w-full text-xs" data-testid="svc-calendar-table">
          <thead><tr className="border-b border-[var(--glass-border)] text-left text-[var(--ink-muted)]">
            <th className="p-3 font-medium">Client</th><th className="p-3 font-medium">Obligation</th><th className="p-3 font-medium">Period</th><th className="p-3 font-medium">Due</th><th className="p-3 font-medium">Status</th><th className="p-3" />
          </tr></thead>
          <tbody className="divide-y divide-[var(--glass-border)]">
            {rows.map((o, i) => (
              <tr key={i} data-testid={`svc-obligation-row-${i}`}>
                <td className="p-3"><div className="font-medium text-[var(--brand)]">{o.client_name}</div><div className="text-[var(--ink-muted)]">{o.client_code}</div></td>
                <td className="max-w-[320px] p-3"><div className="font-medium">{o.doc_label.split("(")[0]}</div><div className="text-[var(--ink-muted)]">{o.detail}</div></td>
                <td className="p-3">{o.period}</td>
                <td className="p-3">{formatDate(o.due_date)}</td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 font-medium ${STATUS_TONE[o.status]}`}>{o.status}</span></td>
                <td className="p-3">
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="outline" className="h-7 rounded-full text-[11px]" onClick={() => preview(o)} data-testid={`svc-preview-${i}`}><FileDown className="mr-1 h-3 w-3" /> PDF</Button>
                    {o.status !== "SENT" && <Button size="sm" className="h-7 rounded-full bg-[var(--cta)] text-[11px] hover:bg-[#087a56]" onClick={() => dispatch(o)} data-testid={`svc-send-${i}`}><Send className="mr-1 h-3 w-3" /> Send</Button>}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-5 text-sm text-[var(--ink-muted)]">Nothing here — all caught up.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}

function VaultTab({ clients }) {
  const [docs, setDocs] = useState([]);
  const [f, setF] = useState({ doc_type: "FACT_SHEET", product: "MF", period: "", target_user_id: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const load = () => adminApi.get("/admin/servicing/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);

  const upload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await adminApi.post("/admin/servicing/documents", {
          file_name: file.name, file_content: reader.result.split(",")[1],
          doc_type: f.doc_type, product: f.product, period: f.period || null,
          target_user_id: f.target_user_id || null, notes: f.notes || null,
        });
        toast.success(`${file.name} uploaded to vault`);
        load();
      } catch (err) { toast.error(getApiErrorMessage(err)); }
      finally { setBusy(false); }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };
  const remove = async (id) => {
    try { await adminApi.delete(`/admin/servicing/documents/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(getApiErrorMessage(err)); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
      <Card className="h-fit border-[var(--glass-border)]"><CardContent className="space-y-3 p-4">
        <div className="text-sm font-medium text-[var(--brand)]">Upload received document</div>
        <div><label className="text-xs text-[var(--ink-muted)]">Type</label>
          <select className="w-full rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={f.doc_type} onChange={(e) => setF({ ...f, doc_type: e.target.value })} data-testid="vault-type-select">
            {["FACT_SHEET", "SOA", "ANNUAL_REPORT", "CAS", "DRAWDOWN_NOTICE", "NAV_STATEMENT", "AUDITED_STATEMENT", "KIM_SID", "OTHER"].map((t) => <option key={t}>{t}</option>)}
          </select></div>
        <div><label className="text-xs text-[var(--ink-muted)]">Product</label>
          <select className="w-full rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={f.product} onChange={(e) => setF({ ...f, product: e.target.value })} data-testid="vault-product-select">
            {["MF", "AIF", "PMS", "GENERAL"].map((t) => <option key={t}>{t}</option>)}
          </select></div>
        <div><label className="text-xs text-[var(--ink-muted)]">Period (e.g. Jun 2026)</label><Input value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })} data-testid="vault-period-input" /></div>
        <div><label className="text-xs text-[var(--ink-muted)]">Visible to</label>
          <select className="w-full rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={f.target_user_id} onChange={(e) => setF({ ...f, target_user_id: e.target.value })} data-testid="vault-target-select">
            <option value="">All clients</option>
            {clients.map((c) => <option key={c.user_id} value={c.user_id}>{c.name}</option>)}
          </select></div>
        <label className={`flex cursor-pointer items-center justify-center gap-1.5 rounded-full bg-[var(--cta)] px-4 py-2 text-sm font-medium text-white ${busy ? "opacity-60" : ""}`}>
          <Upload className="h-4 w-4" /> {busy ? "Uploading..." : "Choose file & upload"}
          <input type="file" accept=".pdf,.csv,.xlsx,.txt" className="hidden" onChange={upload} disabled={busy} data-testid="vault-upload-input" />
        </label>
      </CardContent></Card>
      <Card className="border-[var(--glass-border)]"><CardContent className="p-0">
        <table className="w-full text-xs" data-testid="vault-table">
          <thead><tr className="border-b border-[var(--glass-border)] text-left text-[var(--ink-muted)]">
            <th className="p-3 font-medium">Document</th><th className="p-3 font-medium">Type</th><th className="p-3 font-medium">Product</th><th className="p-3 font-medium">Period</th><th className="p-3 font-medium">Visible to</th><th className="p-3" />
          </tr></thead>
          <tbody className="divide-y divide-[var(--glass-border)]">
            {docs.map((d) => (
              <tr key={d.id} data-testid={`vault-row-${d.id}`}>
                <td className="p-3 font-medium text-[var(--brand)]">{d.file_name}</td>
                <td className="p-3">{d.doc_type}</td>
                <td className="p-3">{d.product}</td>
                <td className="p-3">{d.period || "—"}</td>
                <td className="p-3">{d.target_user_id ? "1 client" : "All clients"}</td>
                <td className="p-3 text-right"><button className="text-red-500" onClick={() => remove(d.id)} data-testid={`vault-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></button></td>
              </tr>
            ))}
            {docs.length === 0 && <tr><td colSpan={6} className="p-5 text-sm text-[var(--ink-muted)]" data-testid="vault-empty">No documents in the vault yet.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}

function LogTab() {
  const [log, setLog] = useState([]);
  useEffect(() => { adminApi.get("/admin/servicing/log").then((r) => setLog(r.data)); }, []);
  return (
    <Card className="border-[var(--glass-border)]"><CardContent className="p-0">
      {log.length === 0 ? <div className="p-5 text-sm text-[var(--ink-muted)]" data-testid="svc-log-empty">No dispatches logged yet.</div> : (
        <table className="w-full text-xs" data-testid="svc-log-table">
          <thead><tr className="border-b border-[var(--glass-border)] text-left text-[var(--ink-muted)]">
            <th className="p-3 font-medium">When</th><th className="p-3 font-medium">Client</th><th className="p-3 font-medium">Document</th><th className="p-3 font-medium">Period</th><th className="p-3 font-medium">Channel</th><th className="p-3 font-medium">By</th>
          </tr></thead>
          <tbody className="divide-y divide-[var(--glass-border)]">
            {log.map((c) => (
              <tr key={c.id}>
                <td className="p-3">{formatDate(c.sent_at)}</td>
                <td className="p-3 font-medium text-[var(--brand)]">{c.client_name} <span className="font-normal text-[var(--ink-muted)]">({c.email})</span></td>
                <td className="p-3">{c.doc_label.split("(")[0]}</td>
                <td className="p-3">{c.period}</td>
                <td className="p-3"><span className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-700">{c.channel}</span></td>
                <td className="p-3">{c.sent_by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </CardContent></Card>
  );
}

export default function Servicing() {
  const [clients, setClients] = useState([]);
  useEffect(() => { adminApi.get("/admin/crm/clients").then((r) => setClients(r.data)); }, []);
  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="flex items-center gap-2 font-heading text-3xl text-[var(--brand)]"><ConciergeBell className="h-7 w-7" /> Client Servicing</h1>
        <p className="mt-1 text-sm text-[var(--ink-muted)]">Statutory communication calendar (SEBI/AMFI), document vault for AMC/RTA/manufacturer documents, and dispatch audit log.</p>
      </div>
      <Tabs defaultValue="calendar">
        <TabsList>
          <TabsTrigger value="calendar" data-testid="svc-tab-calendar">Compliance Calendar</TabsTrigger>
          <TabsTrigger value="vault" data-testid="svc-tab-vault">Document Vault</TabsTrigger>
          <TabsTrigger value="log" data-testid="svc-tab-log">Dispatch Log</TabsTrigger>
        </TabsList>
        <TabsContent value="calendar"><CalendarTab /></TabsContent>
        <TabsContent value="vault"><VaultTab clients={clients} /></TabsContent>
        <TabsContent value="log"><LogTab /></TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
